import { Point, CanvasRenderer, ViewMatch } from '@fullstory/playback-mobile';
import { RendererView } from '@fullstory/tomato-views/views';
import { ScrollNavigation, ScrollModifier, ScrollNavigationEvent } from '../scroll_navigation';

export class MouseWatcher {
  private _mouseMove;
  private _mouseClick;
  private _lastWasDefault: boolean;
  private _lastTarget: ViewMatch;
  private _lastTargets: ViewMatch[];
  private _scrollNavigation: ScrollNavigation;

  constructor(
    private _pagePlaybackView: RendererView,
    private _renderer: CanvasRenderer,
    private _onViewHover: (v: ViewMatch) => void,
    private _onViewClick: (v: ViewMatch) => void,
  ) {}

  private _mapToCanvas(e: MouseEvent): { x: number; y: number } {
    const canvas = this._pagePlaybackView.frame.elem();
    const r = canvas.getBoundingClientRect();
    let x = e.clientX - r.left;
    let y = e.clientY - r.top;
    x *= canvas.offsetWidth / r.width;
    y *= canvas.offsetHeight / r.height;
    return { x, y };
  }

  start() {
    this._scrollNavigation = new ScrollNavigation(window, ScrollModifier.SHIFT, e =>
      this.onScrollEvent(e),
    );
    this._scrollNavigation.start();
    this._mouseClick = (e: MouseEvent) => {
      this._mouseMove(e);
      const view = this._lastTarget;
      if (view) {
        this._onViewClick && this._onViewClick(view);
      }
    };
    this._mouseMove = (e: MouseEvent) => {
      const pt = this._mapToCanvas(e);
      const views = this._renderer.viewsAtPoint(new Point(pt.x, pt.y));
      this._lastTargets = views;

      const view = views[0];

      if (!view) {
        // Nothing selected, so reset
        this._lastTargets = [];
        this._lastTarget = null;
        this._lastWasDefault = true;
        this._onViewHover && this._onViewHover(null);
        return;
      }

      // If the last target is still the most specific view, do nothing
      if (ViewMatch.same(view, this._lastTarget)) {
        return;
      }

      if (this._lastWasDefault) {
        // Previous was default, so we just move to the next default view
        this._lastTarget = view;
      } else {
        // Wasn't default, so try to find the old one
        const index = this.lastIndex();
        if (index === null) {
          // Nope, couldn't find it so use the new default view
          this._lastTarget = view;
          this._lastWasDefault = true;
        } else {
          // Found it, do nothing
        }
      }

      // Called even if view is null
      this._onViewHover && this._onViewHover(this._lastTarget);
    };

    document.body.addEventListener('click', this._mouseClick, { capture: true });
    document.body.addEventListener('mousemove', this._mouseMove, { capture: true });
  }

  stopAndDispose() {
    this._scrollNavigation.stop();
    document.body.removeEventListener('click', this._mouseClick);
    document.body.removeEventListener('mousemove', this._mouseMove);
  }

  private lastIndex(): number | null {
    for (let i = 0; i < this._lastTargets.length; i++) {
      if (ViewMatch.same(this._lastTarget, this._lastTargets[i])) {
        return i;
      }
    }

    return null;
  }

  private onScrollEvent(e: ScrollNavigationEvent) {
    if (e != ScrollNavigationEvent.SCROLL_UP && e != ScrollNavigationEvent.SCROLL_DOWN) {
      return;
    }

    // Sanity check
    if (!this._lastTargets || this._lastTargets.length == 0) {
      return;
    }

    // Update the index in the scroll direction
    let index = this.lastIndex();
    this._lastWasDefault = false;

    if (e == ScrollNavigationEvent.SCROLL_UP) {
      if (index === null) {
        index = 0;
      } else {
        index -= 1;
      }
      if (index < 0) {
        index = 0;
      }
    } else if (e == ScrollNavigationEvent.SCROLL_DOWN) {
      if (index === null) {
        index = this._lastTargets.length - 1;
      } else {
        index += 1;
      }
      if (index > this._lastTargets.length - 1) {
        index = this._lastTargets.length - 1;
      }
    }

    this._lastTarget = this._lastTargets[index];
    // Called even if view is null
    this._onViewHover && this._onViewHover(this._lastTarget);
  }
}
